import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerTokenService } from '../../services/server-token.service';

export interface RegionFacility {
  _id: string;
  displayName: string;
  facilityId: number;
  prov: string;
  name: string;
  facility_code: string;
}

export interface FacilityAssignmentDoc {
  _id: string;
  srcType: string;
  siteName: string;
  facility_settings_id: string | null;
  hidden?: boolean;
}

export interface FacilityAssignmentLoadData {
  facilities: RegionFacility[];
  assignments: FacilityAssignmentDoc[];
}

export interface AssignmentExpected {
  facility_settings_id: string | null;
  hidden: boolean;
}

export interface UpdateAssignmentResult {
  success: boolean;
  updatedPatientsCount: number;
}

function withExpected<T extends object>(body: T, expected?: AssignmentExpected): T {
  return expected
    ? { ...body, expected_facility_settings_id: expected.facility_settings_id, expected_hidden: expected.hidden }
    : body;
}

@Injectable({ providedIn: 'root' })
export class FacilityAssignmentService {
  constructor(private server: ServerTokenService) {}

  updateAssignment(
    assignmentId: string,
    facilitySettingsId: string | null,
    expected?: AssignmentExpected,
  ): Observable<UpdateAssignmentResult> {
    return this.server.patchWithToken<UpdateAssignmentResult>(
      `snfs/admin/facility-assignment/${assignmentId}`,
      withExpected({ facility_settings_id: facilitySettingsId }, expected),
    );
  }

  setHidden(assignmentId: string, hidden: boolean, expected?: AssignmentExpected): Observable<UpdateAssignmentResult> {
    return this.server.patchWithToken<UpdateAssignmentResult>(
      `snfs/admin/facility-assignment/${assignmentId}`,
      withExpected({ facility_settings_id: null, hidden }, expected),
    );
  }

  loadAll(): Observable<FacilityAssignmentLoadData> {
    return forkJoin({
      facilities: this.server.getWithToken<{ facilities: RegionFacility[] }>('snfs/admin/facility-assignment/region-facilities')
        .pipe(map(res => res.facilities)),
      assignments: this.server.getWithToken<{ assignments: FacilityAssignmentDoc[] }>('snfs/admin/facility-assignment/assignments')
        .pipe(map(res => res.assignments)),
    });
  }
}
